/**
 * CSV/XLSX Export Modal — Inventaire.
 *
 * Lets the user pick:
 *  - scope : all | boutique | depot
 *  - format: csv | xlsx
 *  - include_zero : if true, articles with 0 in the chosen scope are kept.
 *
 * Calls GET /api/stock/export with axios responseType=blob then triggers a
 * client-side download. Works only online (export is a server-side query
 * that has no offline meaning).
 */
import React, { useState } from 'react';
import {
  X, Download, FileSpreadsheet, Store, Warehouse, Loader2, Boxes,
} from 'lucide-react';

const SCOPES = [
  { id: 'all', label: 'Tout le stock', icon: Boxes, hint: 'Boutique + Dépôt' },
  { id: 'boutique', label: 'Boutique uniquement', icon: Store, hint: 'Articles vendus en magasin' },
  { id: 'depot', label: 'Dépôt uniquement', icon: Warehouse, hint: 'Stock de réserve' },
];

const FORMATS = [
  { id: 'csv', label: 'CSV (.csv)', hint: 'Compatible Excel / Google Sheets' },
  { id: 'xlsx', label: 'Excel (.xlsx)', hint: 'Format natif Microsoft Excel' },
];

const CSVExportModal = ({ open, onClose, api }) => {
  const [scope, setScope] = useState('all');
  const [format, setFormat] = useState('csv');
  const [includeZero, setIncludeZero] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const downloadExport = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await api.get('/stock/export', {
        params: { scope, format, include_zero: includeZero },
        responseType: 'blob',
      });
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '_');
      const filename = `stock_${scope}_${ts}.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail
        || (navigator.onLine ? 'Erreur lors de l\'export' : 'Export indisponible hors-ligne (action serveur)');
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur flex items-center justify-center p-4"
      data-testid="csv-export-modal"
    >
      <div className="w-full max-w-lg bg-gray-950 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        <header className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-6 h-6 text-emerald-400" />
            <div>
              <h2 className="text-base font-semibold">Exporter l'inventaire</h2>
              <p className="text-xs text-gray-500">Téléchargez le stock filtré au format CSV ou Excel</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-white/5" data-testid="csv-export-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-4 space-y-5">
          {/* Scope */}
          <div>
            <div className="text-xs uppercase text-gray-500 mb-2 tracking-wider">Périmètre</div>
            <div className="grid grid-cols-1 gap-2">
              {SCOPES.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    onClick={() => setScope(s.id)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-colors ${
                      scope === s.id
                        ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-100'
                        : 'bg-white/[0.03] border-white/10 text-gray-300 hover:bg-white/[0.06]'
                    }`}
                    data-testid={`csv-export-scope-${s.id}`}
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{s.label}</div>
                      <div className="text-[11px] text-gray-500">{s.hint}</div>
                    </div>
                    <span className={`w-2.5 h-2.5 rounded-full ${scope === s.id ? 'bg-emerald-400' : 'bg-white/10'}`} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Format */}
          <div>
            <div className="text-xs uppercase text-gray-500 mb-2 tracking-wider">Format</div>
            <div className="grid grid-cols-2 gap-2">
              {FORMATS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFormat(f.id)}
                  className={`px-3 py-2.5 rounded-xl border text-left transition-colors ${
                    format === f.id
                      ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-100'
                      : 'bg-white/[0.03] border-white/10 text-gray-300 hover:bg-white/[0.06]'
                  }`}
                  data-testid={`csv-export-format-${f.id}`}
                >
                  <div className="text-sm font-medium">{f.label}</div>
                  <div className="text-[11px] text-gray-500">{f.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Options */}
          <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={includeZero}
              onChange={(e) => setIncludeZero(e.target.checked)}
              className="accent-emerald-500"
              data-testid="csv-export-include-zero"
            />
            Inclure les articles avec quantité = 0 dans ce périmètre
          </label>

          {error && (
            <div className="bg-red-500/15 border border-red-500/30 text-red-200 rounded-xl p-3 text-sm" data-testid="csv-export-error">
              {error}
            </div>
          )}
        </div>

        <footer className="p-4 border-t border-white/10 flex items-center justify-between gap-3">
          <span className="text-[11px] text-gray-500">
            Le fichier est généré côté serveur — connexion internet requise.
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-sm"
              data-testid="csv-export-cancel"
            >
              Annuler
            </button>
            <button
              onClick={downloadExport}
              disabled={busy}
              className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-40 text-sm font-medium flex items-center gap-2"
              data-testid="csv-export-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Télécharger
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default CSVExportModal;
